/**
 * Mermaid theme for every diagram Coco renders, wired in via
 * `mermaid={{ config }}` (see ../../components/ai-elements/response-markdown.ts).
 *
 * Mermaid's stock `default` theme is lavender-on-white with monospace labels —
 * the one piece of Coco's chat that ignores the app's palette entirely. This
 * restates it on the V1 tokens (apps/client/src/app/v1/v1.css): warm sand
 * surfaces, slate ink, and the terracotta→amber chart arc for anything
 * categorical.
 *
 * ## Why these are hex literals and not `var(--secondary)`
 *
 * Mermaid's `base` theme doesn't hand our values straight to the SVG — it
 * *derives* from them, running khroma's `darken`/`lighten`/`invert`/`adjust`
 * over whatever we pass to fill in the variables we leave unset. khroma 2.x
 * parses hex, `rgb()`, `hsl()` and named colors only: it throws
 * `Unsupported color format` on both `var(--secondary)` and the raw
 * `oklch(...)` the tokens are authored in. So the tokens are transcribed here
 * as their sRGB hex equivalents, each labelled with the variable it mirrors.
 * Change a color in v1.css and it has to be changed here too.
 *
 * ## Scope
 *
 * This is a module constant applied to every `Response`, which is V1's chat
 * plus one V0 surface (execution-flow-display). V1 is light-only; V0's `.dark`
 * mode would show these light surfaces unchanged, which is the known cost of a
 * static config — Streamdown reads `mermaid` off React context, so a palette
 * that varied per shell would invalidate every memoized block on every
 * streamed token.
 */

// Re-exported by Streamdown from `mermaid`, which is its transitive dependency
// and so isn't resolvable from the client's own module graph.
import type { MermaidConfig } from 'streamdown';

/**
 * V1 tokens as sRGB hex. Names track the CSS custom property, not the color,
 * so a token that gets retuned keeps its call sites honest.
 */
const V1 = {
  /** `--v1-canvas` / `--background` — the app's warm paper ground. */
  canvas: '#f8f7f4',
  /** `--card` — the white a page you write on is. */
  card: '#fefefe',
  /** `--secondary` / `--sidebar-accent` — the raised warm surface. */
  sand: '#f0ede8',
  /** `--border` / `--input`. */
  border: '#eae8e0',
  /** `--ring` — a step darker than `--border`. */
  ring: '#dcd7c9',
  /** `--foreground` — slate-navy body text. */
  ink: '#2d3e56',
  /** `--muted-foreground` — secondary text, and here every line and arrow. */
  slate: '#48516c',
} as const;

/**
 * `--chart-1` … `--chart-10`, each with its `-bg` wash and `-fg` dark text.
 * The warm terracotta → amber → rose arc; index 0 is `--chart-1`.
 */
const CHART = [
  { main: '#b67168', bg: '#fceeec', fg: '#5e3530' }, // terracotta
  { main: '#c79170', bg: '#fbefe9', fg: '#613e28' }, // peach
  { main: '#ca9a65', bg: '#f9f0e7', fg: '#573c1c' }, // amber
  { main: '#c4aa7b', bg: '#f6f1e9', fg: '#504022' }, // wheat
  { main: '#ba7b82', bg: '#fceeef', fg: '#5e343a' }, // rose
  { main: '#a36957', bg: '#f8ebe7', fg: '#583125' }, // clay
  { main: '#ba8e6d', bg: '#f8f0ea', fg: '#573c27' }, // apricot
  { main: '#a26f82', bg: '#f9eff2', fg: '#52323e' }, // dusty rose
  { main: '#ceb38d', bg: '#f9f4ee', fg: '#584428' }, // sand
  { main: '#956451', bg: '#f8ebe7', fg: '#4e2f23' }, // umber
] as const;

/**
 * The chart arc pre-lightened by 25 HSL points, in the 12 slots mermaid's
 * `THEME_COLOR_LIMIT` asks for (the arc, then two repeats).
 *
 * `cScale*` and `git*` are the two families the `base` theme darkens by 25
 * *unconditionally* — the `this.x = this.x || …` guard covers the default only,
 * and the `darken` loop that follows runs over whatever survived, ours
 * included. Passing `CHART[n].main` here would land a visibly muddier color
 * than the same token shows anywhere else in the app, so these are lightened by
 * the amount mermaid is about to remove. `mermaid-theme.spec.ts` asserts the
 * round trip, so a mermaid or khroma bump that drops the loop fails loudly
 * rather than quietly washing every journey and gitGraph out.
 */
export const CHART_SCALE_PRELIGHTENED = [
  '#e0c2be', // → --chart-1
  '#ebd7cc', // → --chart-2
  '#ebd8c4', // → --chart-3
  '#ebe3d3', // → --chart-4
  '#e6cfd1', // → --chart-5
  '#d1b2a9', // → --chart-6
  '#e3d1c4', // → --chart-7
  '#d4bcc5', // → --chart-8
  '#f4eee6', // → --chart-9
  '#c9a99c', // → --chart-10
  '#ebd8c4', // → --chart-3
  '#d4bcc5', // → --chart-8
] as const;

/** Spreads one prefixed, index-suffixed family (`pie1`, `cScale0`, …). */
function indexed(
  prefix: string,
  values: readonly string[],
  startAt: number
): Record<string, string> {
  return Object.fromEntries(
    values.map((value, i) => [`${prefix}${i + startAt}`, value])
  );
}

/**
 * Passed to `mermaid.initialize` by Streamdown, merged over its own defaults
 * (`startOnLoad: false`, `securityLevel: 'strict'`, `suppressErrorRendering`,
 * and a `fontFamily: 'monospace'` this deliberately overrides).
 *
 * `theme: 'base'` is the only mermaid theme that reads `themeVariables` at all;
 * `default`, `neutral`, `forest` and `dark` ignore them.
 */
export const MERMAID_THEME_CONFIG: MermaidConfig = {
  theme: 'base',
  themeVariables: {
    // Labels follow the app, not the code block they were authored in.
    fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
    fontSize: '14px',

    // --- Ground, ink, and the three semantic ramps -----------------------
    background: V1.canvas,
    primaryColor: V1.sand,
    primaryTextColor: V1.ink,
    primaryBorderColor: CHART[0].main,
    secondaryColor: CHART[2].bg,
    secondaryTextColor: CHART[2].fg,
    secondaryBorderColor: CHART[2].main,
    tertiaryColor: V1.canvas,
    tertiaryTextColor: V1.ink,
    tertiaryBorderColor: V1.ring,
    textColor: V1.ink,
    titleColor: V1.ink,
    lineColor: V1.slate,
    arrowheadColor: V1.slate,

    // --- Flowchart -------------------------------------------------------
    mainBkg: V1.sand,
    nodeBkg: V1.sand,
    nodeBorder: CHART[0].main,
    nodeTextColor: V1.ink,
    // A subgraph is a quieter ground *behind* its nodes, so it takes the
    // canvas while the nodes keep the raised sand.
    clusterBkg: V1.canvas,
    clusterBorder: V1.ring,
    defaultLinkColor: V1.slate,
    // Punches the label out of the line it sits on.
    edgeLabelBackground: V1.card,

    // --- Notes -----------------------------------------------------------
    noteBkgColor: CHART[3].bg,
    noteTextColor: CHART[3].fg,
    noteBorderColor: CHART[3].main,

    // --- Sequence --------------------------------------------------------
    actorBkg: V1.sand,
    actorBorder: CHART[0].main,
    actorTextColor: V1.ink,
    actorLineColor: V1.ring,
    signalColor: V1.slate,
    signalTextColor: V1.ink,
    labelBoxBkgColor: CHART[2].bg,
    labelBoxBorderColor: CHART[2].main,
    labelTextColor: CHART[2].fg,
    loopTextColor: V1.ink,
    activationBkgColor: CHART[1].bg,
    activationBorderColor: CHART[1].main,
    // Reads on top of a circle filled with `lineColor`.
    sequenceNumberColor: V1.card,

    // --- State / class ---------------------------------------------------
    stateBkg: V1.sand,
    labelBackgroundColor: V1.card,
    altBackground: V1.canvas,
    compositeBackground: V1.canvas,
    compositeTitleBackground: V1.sand,
    compositeBorder: CHART[0].main,
    classText: V1.ink,
    transitionColor: V1.slate,
    transitionLabelColor: V1.ink,

    // --- Gantt -----------------------------------------------------------
    sectionBkgColor: CHART[3].bg,
    sectionBkgColor2: CHART[1].bg,
    altSectionBkgColor: V1.card,
    excludeBkgColor: V1.border,
    gridColor: V1.border,
    taskBkgColor: V1.sand,
    taskBorderColor: CHART[0].main,
    taskTextColor: V1.ink,
    taskTextDarkColor: V1.ink,
    taskTextLightColor: V1.card,
    taskTextOutsideColor: V1.ink,
    activeTaskBkgColor: CHART[2].bg,
    activeTaskBorderColor: CHART[2].main,
    doneTaskBkgColor: V1.border,
    doneTaskBorderColor: V1.ring,
    critBkgColor: CHART[4].bg,
    critBorderColor: CHART[4].main,
    todayLineColor: CHART[0].main,

    // --- Errors ----------------------------------------------------------
    errorBkgColor: CHART[4].bg,
    errorTextColor: CHART[4].fg,

    // --- Pie -------------------------------------------------------------
    // `pie*` is *not* darkened downstream (unlike cScale/git), so it takes the
    // chart tokens as authored. Opacity goes to 1 because the arc is already
    // soft — mermaid's 0.7 default washes it into the ground.
    ...indexed(
      'pie',
      [...CHART.map((c) => c.main), CHART[2].main, CHART[7].main],
      1
    ),
    pieOpacity: '1',
    pieStrokeColor: V1.card,
    pieOuterStrokeColor: V1.ring,
    pieTitleTextColor: V1.ink,
    pieSectionTextColor: V1.card,
    pieLegendTextColor: V1.ink,

    // --- Categorical scales (journey, timeline, quadrant, requirement) ----
    ...indexed('cScale', CHART_SCALE_PRELIGHTENED, 0),
    scaleLabelColor: V1.ink,

    // --- gitGraph --------------------------------------------------------
    ...indexed('git', CHART_SCALE_PRELIGHTENED.slice(0, 8), 0),
    branchLabelColor: V1.ink,
    commitLabelColor: V1.ink,
    commitLabelBackground: V1.card,
    tagLabelColor: V1.ink,
    tagLabelBackground: CHART[3].bg,
    tagLabelBorder: CHART[3].main,

    // --- xychart ---------------------------------------------------------
    xyChart: {
      backgroundColor: V1.canvas,
      titleColor: V1.ink,
      xAxisLabelColor: V1.slate,
      xAxisTitleColor: V1.ink,
      xAxisTickColor: V1.ring,
      xAxisLineColor: V1.ring,
      yAxisLabelColor: V1.slate,
      yAxisTitleColor: V1.ink,
      yAxisTickColor: V1.ring,
      yAxisLineColor: V1.ring,
      plotColorPalette: CHART.map((c) => c.main).join(','),
    },
  },
};
